import type {
  PersonalityTraits
} from '../services/api'

// 5 discrete levels per trait, matching backend TRAIT_DESCRIPTORS order.
// Each level has a short label and a description shown when selected.
export const TRAIT_LEVELS: Record<keyof PersonalityTraits, { label: string; description: string }[]> = {
  openness: [
    { label: 'Conventional', description: 'Grounded and practical, preferring tried-and-true approaches' },
    { label: 'Pragmatic', description: 'Pragmatic, leaning toward familiar solutions while open to solid evidence' },
    { label: 'Balanced', description: 'Balanced between conventional thinking and creative exploration' },
    { label: 'Curious', description: 'Curious and imaginative, enjoys exploring new angles' },
    { label: 'Inventive', description: 'Highly inventive and intellectually adventurous, thrives on novel ideas' },
  ],
  conscientiousness: [
    { label: 'Spontaneous', description: 'Spontaneous and flexible, going with the flow' },
    { label: 'Relaxed', description: 'Relaxed about structure, preferring flexibility over rigid plans' },
    { label: 'Balanced', description: 'Reasonably organized while staying adaptable' },
    { label: 'Disciplined', description: 'Disciplined and well-organized, follows through reliably' },
    { label: 'Methodical', description: 'Exceptionally methodical and precise, leaves nothing to chance' },
  ],
  extraversion: [
    { label: 'Reserved', description: 'Quiet and reserved, preferring depth over breadth' },
    { label: 'Measured', description: 'Somewhat reserved, speaking up when it matters' },
    { label: 'Balanced', description: 'Comfortable in conversation without dominating it' },
    { label: 'Sociable', description: 'Sociable and energetic, bringing warmth to interactions' },
    { label: 'Outgoing', description: 'Highly outgoing and enthusiastic, radiating positive energy' },
  ],
  agreeableness: [
    { label: 'Challenging', description: 'Blunt and challenging, prioritizing truth over comfort' },
    { label: 'Candid', description: 'Candid and direct, willing to push back when needed' },
    { label: 'Diplomatic', description: 'Fair and balanced, diplomatic but not a pushover' },
    { label: 'Cooperative', description: 'Warm and cooperative, putting relationships first' },
    { label: 'Nurturing', description: 'Deeply caring and selflessly accommodating' },
  ],
  sensitivity: [
    { label: 'Thick-skinned', description: 'Thick-skinned and steady under pressure, keeps emotions out of decisions' },
    { label: 'Composed', description: 'Generally composed, taking most things in stride' },
    { label: 'Balanced', description: 'Emotionally balanced, aware of feelings without being ruled by them' },
    { label: 'Perceptive', description: 'Sensitive and perceptive, attuned to emotional undercurrents' },
    { label: 'Empathetic', description: 'Deeply empathetic and emotionally attuned, feels the weight of every interaction' },
  ],
}

// Field limits, matching the backend's CreateCustomCharacterRequest.
export const NAME_MAX_LENGTH = 100
export const BIO_MAX_LENGTH = 500

export const TRAIT_KEYS = Object.keys(TRAIT_LEVELS) as (keyof PersonalityTraits)[]

// Slider snaps to these 5 values, which map to backend levels 0-4
export const LEVEL_VALUES: number[] = [0, 25, 50, 75, 100]

export function valueToLevel(value: number): number {
  // Snap any stored value to the nearest level index
  let best = 0
  for (let i = 1; i < LEVEL_VALUES.length; i++) {
    if (Math.abs(LEVEL_VALUES[i]! - value) < Math.abs(LEVEL_VALUES[best]! - value)) {
      best = i
    }
  }
  return best
}
